import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus, ShieldAlert } from "lucide-react";
import { api, ApiError, qs } from "@/lib/api";
import type { AdminUser, AuditEntry, Me, Setting, SystemStatus } from "@/lib/types";
import { formatAgo, formatDateTime } from "@/lib/format";
import { applyTheme, type Theme } from "@/lib/theme";
import { useIsAdmin } from "@/lib/session";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { ReleaseLabel } from "@/components/ReleaseLabel";
import { WorkerBadges } from "@/components/WorkerBadges";

/** Your own preferences and the people who can sign in. */
export function AccountPanel({ me }: { me: Me }) {
  const qc = useQueryClient();
  const toast = useToast();
  const users = useQuery({ queryKey: ["users"], queryFn: () => api<{ users: AdminUser[] }>("/api/v1/users") });
  const isAdmin = me.role === "admin";
  const [inviting, setInviting] = useState<InviteStart | null>(null);
  const [disabling, setDisabling] = useState<AdminUser | null>(null);
  const theme: Theme = me.preferences?.theme === "light" ? "light" : "dark";
  const setTheme = async (t: Theme) => {
    applyTheme(t);
    qc.setQueryData(["me"], { ...me, preferences: { ...me.preferences, theme: t } });
    await api("/api/v1/auth/me/preferences", { method: "PUT", json: { theme: t } }).then(
      () => toast(t === "dark" ? "Dark mode saved" : "Light mode saved"),
      (e: Error) => toast(e.message, "error"),
    );
  };
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>You</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            {me.name} <span className="text-[var(--color-muted-foreground)]">({me.email}, {me.role})</span>
          </div>
          <div role="radiogroup" aria-label="Theme" className="flex items-center gap-2">
            <span className="text-[var(--color-muted-foreground)]">Screens:</span>
            <Button size="sm" role="radio" aria-checked={theme === "dark"} variant={theme === "dark" ? "default" : "outline"} onClick={() => void setTheme("dark")}>
              Dark (default)
            </Button>
            <Button size="sm" role="radio" aria-checked={theme === "light"} variant={theme === "light" ? "default" : "outline"} onClick={() => void setTheme("light")}>
              Light
            </Button>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">Saved to your sign-in, so it follows you to any computer. Times everywhere are Mountain Time.</p>
        </CardContent>
      </Card>
      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>People who can sign in</CardTitle>
          {isAdmin && (
            <Button size="sm" onClick={() => setInviting({})}>
              <Plus /> Invite someone
            </Button>
          )}
        </CardHeader>
        {users.isLoading && <Skeleton className="m-3 h-20" />}
        {users.data && (
          <Table>
            <THead>
              <tr>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Last signed in</TH>
                {isAdmin && <TH className="w-px" />}
              </tr>
            </THead>
            <tbody>
              {users.data.users.map((u) => {
                const waiting = !u.disabled_at && !u.last_login_at && !u.totp_enabled_at;
                return (
                  <TR key={u.id} className={u.disabled_at ? "row-dim" : ""}>
                    <TD className="font-medium">
                      {u.name}
                      {u.id === me.id && <span className="ml-1.5 text-xs font-normal text-[var(--color-muted-foreground)]">(you)</span>}
                    </TD>
                    <TD>{u.email}</TD>
                    <TD>{u.role}</TD>
                    <TD>
                      {u.disabled_at ? (
                        <Badge variant="muted" title={`Since ${formatDateTime(u.disabled_at)}`}>
                          disabled
                        </Badge>
                      ) : waiting ? (
                        <Badge variant="warning" title="They have a set-up link and haven't used it yet">
                          invited, not set up yet
                        </Badge>
                      ) : u.totp_enabled_at ? (
                        <Badge variant="success">two-step sign-in on</Badge>
                      ) : (
                        <Badge variant="warning">two-step sign-in at next sign-in</Badge>
                      )}
                    </TD>
                    <TD title={formatDateTime(u.last_login_at)}>{u.last_login_at ? formatAgo(u.last_login_at) : "never"}</TD>
                    {isAdmin && (
                      <TD className="whitespace-nowrap text-right">
                        {waiting && (
                          <Button size="sm" variant="ghost" onClick={() => setInviting({ email: u.email, name: u.name, role: u.role, newLink: true })}>
                            New link
                          </Button>
                        )}
                        {!u.disabled_at && u.id !== me.id && (
                          <Button size="sm" variant="ghost" onClick={() => setDisabling(u)}>
                            Disable
                          </Button>
                        )}
                      </TD>
                    )}
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
        <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
          An invite makes a one-time set-up link: the person opens it, picks their own password and sets up two-step sign-in, so no password passes through anyone else.
        </p>
      </Card>
      {inviting && <InviteDialog start={inviting} onClose={() => setInviting(null)} />}
      {disabling && <DisableDialog user={disabling} onClose={() => setDisabling(null)} />}
    </div>
  );
}

interface InviteStart {
  email?: string;
  name?: string;
  role?: string;
  newLink?: boolean;
}

const ROLES: [string, string][] = [
  ["staff", "Staff: read and change data, no money"],
  ["readonly", "Read-only: look, change nothing"],
  ["admin", "Admin: everything, including users and tokens"],
];

/** users_invite: makes the account (no password) and shows its one-time set-up link once. */
function InviteDialog({ start, onClose }: { start: InviteStart; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState(start.email ?? "");
  const [name, setName] = useState(start.name ?? "");
  const [role, setRole] = useState(start.role ?? "staff");
  const [made, setMade] = useState<{ email: string; link: string; expires: string } | null>(null);
  const invite = useMutation({
    mutationFn: () =>
      api<{ email: string; link: string; expires: string; expires_at: string }>("/api/v1/users/invite", {
        json: { email: email.trim(), name: name.trim(), role, new_link: start.newLink || undefined },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      setMade({ email: r.email, link: r.link, expires: r.expires || formatDateTime(r.expires_at) });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const copy = () => void navigator.clipboard?.writeText(made!.link).then(() => toast("Link copied"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>{made ? "Set-up link for " + made.email : start.newLink ? `New set-up link for ${start.email}` : "Invite someone"}</DialogTitle>
        {made ? (
          <>
            <DialogDescription>
              Shown only now. It works once, until {made.expires}. Send it to them privately; when they open it they pick a password and set up two-step sign-in.
            </DialogDescription>
            <div className="flex gap-2">
              <Input readOnly value={made.link} aria-label="Set-up link" onFocus={(e) => e.currentTarget.select()} />
              <Button variant="outline" onClick={copy}>
                Copy
              </Button>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <DialogDescription>
              {start.newLink
                ? "Their old link stops working. The new one works once, for 24 hours."
                : "This makes their account with no password and a one-time set-up link, good for 24 hours."}
            </DialogDescription>
            <div className="space-y-3 text-sm">
              <div className="space-y-1">
                <Label htmlFor="inv-email">Email (their sign-in name)</Label>
                <Input id="inv-email" type="email" value={email} disabled={!!start.newLink} onChange={(e) => setEmail(e.target.value)} placeholder="name@creuniversity.com" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inv-name">Name</Label>
                <Input id="inv-name" value={name} disabled={!!start.newLink} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inv-role">What they can do</Label>
                <Select id="inv-role" value={role} disabled={!!start.newLink} onChange={(e) => setRole(e.target.value)}>
                  {ROLES.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={invite.isPending || !email.includes("@") || !name.trim()} onClick={() => invite.mutate()}>
                {start.newLink ? "Make a new link" : "Invite"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** users_disable: they can't sign in, their sessions end and an unused set-up link stops working. */
function DisableDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const disable = useMutation({
    mutationFn: () => api<{ message: string; live_tokens: number; sessions_ended: number }>(`/api/v1/users/${user.id}/disable`, { method: "POST" }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      toast(r.message || `Disabled ${user.email}`, r.live_tokens > 0 ? "error" : "ok");
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-2rem))]">
        <DialogTitle>Disable {user.name}?</DialogTitle>
        <DialogDescription>
          {user.email} won't be able to sign in. Anyone signed in as them is signed out now, and an unused set-up link stops working. API and MCP tokens they made keep working
          until you revoke them under API and MCP tokens.
        </DialogDescription>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={disable.isPending} onClick={() => disable.mutate()}>
            Disable
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Settings the server guards (audit H-2): admin only, allowed values only, and a confirm step. */
const SENSITIVE = /^(billing|email|keap|affiliates|zapier)\.|^ga4\.enabled$/;

const EMAIL_MODES = [
  { value: "off", label: "Off (nothing sent)" },
  { value: "preview", label: "Preview (copies to the preview address only)" },
  { value: "live", label: "Live (sent to customers)" },
];

/** Settings with a fixed set of answers get a picker instead of free JSON. */
const CHOICES: Record<string, { value: string; label: string }[]> = {
  // Brandon, 2026-10-27: card payments move to Stripe; PayPal and Payflow are dropped.
  "billing.active_gateway": [
    { value: "stripe", label: "Stripe" },
    { value: "authorize_net", label: "Authorize.net" },
  ],
  "billing.customer_emails": EMAIL_MODES,
  "billing.cart_reminders": EMAIL_MODES,
  "billing.ended_emails": EMAIL_MODES,
  "billing.renewal_notices": EMAIL_MODES,
  "billing.card_expiry_notices": EMAIL_MODES,
};

/** PayPal and Payflow are switched off (Brandon, 2026-10-27); billing keeps their code dormant. Their switches stay
 *  off and out of this list so nobody turns them back on by accident. */
const DORMANT = new Set(["billing.paypal_enabled", "billing.payflow_enabled", "billing.failover"]);
const isDormant = (key: string) => DORMANT.has(key.trim());

/** CRM settings (key → JSON value), each change audited. */
export function SettingsPanel() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => api<{ settings: Setting[] }>("/api/v1/settings") });
  const [editing, setEditing] = useState<Setting | "new" | null>(null);
  const all = q.data?.settings ?? [];
  const rows = all.filter((s) => !isDormant(s.key));
  const hidden = all.length - rows.length;
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>CRM settings</CardTitle>
        {isAdmin && (
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus /> Add a setting
          </Button>
        )}
      </CardHeader>
      {!isAdmin && <p className="px-4 pt-2 text-xs text-[var(--color-warning-fg)]">Only an admin can change these. You can look.</p>}
      <p className="px-4 py-2 text-sm text-[var(--color-muted-foreground)]">
        Switches the modules read (billing run times, email warm-up stage…). Each module documents its own keys; every change is in the audit log.
        {hidden > 0 && ` PayPal and Payflow are switched off; their ${hidden === 1 ? "setting is" : `${hidden} settings are`} hidden.`}
      </p>
      {q.isLoading && <Skeleton className="m-3 h-20" />}
      {!q.isLoading && rows.length === 0 && <Empty title="No settings stored yet.">The modules use their built-in defaults until a setting is added here.</Empty>}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Setting</TH>
              <TH>Value</TH>
              <TH>What it does</TH>
              <TH>Changed</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {rows.map((s) => (
              <TR key={s.key}>
                <TD className="num whitespace-nowrap font-medium">{s.key}</TD>
                <TD className="max-w-[18rem]">
                  {CHOICES[s.key] ? (
                    <span className="text-sm">{CHOICES[s.key].find((c) => c.value === s.value)?.label ?? <code className="text-xs">{JSON.stringify(s.value)}</code>}</span>
                  ) : (
                    <code className="block truncate text-xs">{JSON.stringify(s.value)}</code>
                  )}
                </TD>
                <TD className="max-w-[20rem] text-xs text-[var(--color-muted-foreground)]">{s.description ?? "—"}</TD>
                <TD className="whitespace-nowrap text-xs" title={formatDateTime(s.updated_at)}>
                  {formatAgo(s.updated_at)}
                  {s.updated_by ? ` by ${s.updated_by}` : ""}
                </TD>
                <TD className="text-right">
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>
                      Change
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {editing && (
        <SettingDialog
          setting={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(k) => {
            void qc.invalidateQueries({ queryKey: ["settings"] });
            toast(`Saved ${k}`);
          }}
        />
      )}
    </Card>
  );
}

function SettingDialog({ setting, onClose, onSaved }: { setting: Setting | null; onClose: () => void; onSaved: (key: string) => void }) {
  const [key, setKey] = useState(setting?.key ?? "");
  const [value, setValue] = useState(setting ? JSON.stringify(setting.value, null, 2) : "");
  const [description, setDescription] = useState(setting?.description ?? "");
  const choices = CHOICES[key.trim()];
  const dormant = isDormant(key);
  let parsed: unknown;
  let parseError = "";
  try {
    parsed = value.trim() ? JSON.parse(value) : undefined;
  } catch (e) {
    parseError = (e as Error).message;
  }
  // Guarded settings answer confirm_required first: show what the change does, then save with confirm.
  const [ask, setAsk] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (confirm: boolean) => {
      try {
        await api(`/api/v1/settings/${encodeURIComponent(key)}`, {
          method: "PUT",
          json: { value: parsed, description: description || undefined, confirm: confirm || undefined },
        });
        return true;
      } catch (e) {
        if (!confirm && e instanceof ApiError && e.code === "confirm_required") {
          setAsk(e.message);
          return false;
        }
        throw e;
      }
    },
    onMutate: () => setAsk(null),
    onSuccess: (saved) => {
      if (!saved) return;
      onSaved(key);
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{setting ? `Change ${setting.key}` : "Add a setting"}</DialogTitle>
        <DialogDescription>
          {choices ? "Pick one of the allowed values." : 'The value is JSON: a number (3), text in quotes ("07:00"), true/false, a list ([1, 2]) or an object.'}
        </DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(false);
          }}
        >
          {!setting && (
            <div className="space-y-1">
              <Label htmlFor="set-key">Key (module.name)</Label>
              <Input id="set-key" autoFocus value={key} onChange={(e) => setKey(e.target.value)} placeholder="email.warmup_stage" pattern="[a-z0-9_]+\.[a-z0-9_.]+" required />
            </div>
          )}
          {choices ? (
            <div className="space-y-1">
              <Label htmlFor="set-val">Value</Label>
              <Select id="set-val" value={typeof parsed === "string" ? parsed : ""} onChange={(e) => setValue(JSON.stringify(e.target.value))} autoFocus={!!setting}>
                <option value="" disabled>
                  Choose…
                </option>
                {choices.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              {key.trim() === "billing.active_gateway" && (
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  New card payments and card updates go to this gateway. Saved cards keep renewing on the gateway that saved them.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="set-val">Value</Label>
              <Textarea id="set-val" className="min-h-24 font-mono text-xs" value={value} onChange={(e) => setValue(e.target.value)} autoFocus={!!setting} />
              {parseError && value.trim() && <p className="text-xs text-[var(--color-destructive)]">Not valid JSON yet: {parseError}</p>}
            </div>
          )}
          {dormant && (
            <p className="text-xs text-[var(--color-danger-fg)]">PayPal and Payflow are switched off (Brandon, 2026-10-27). This setting stays off; it can't be changed here.</p>
          )}
          <div className="space-y-1">
            <Label htmlFor="set-desc">What it does</Label>
            <Input id="set-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {SENSITIVE.test(key) && (
            <p className="flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> This changes how billing, email or Keap runs, starting with the next run. You'll be asked to confirm.
            </p>
          )}
          {ask && (
            <div role="alert" className="space-y-2 rounded-md bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-fg)]">
              <p>{ask}</p>
              <Button type="button" variant="destructive" size="sm" disabled={save.isPending} onClick={() => save.mutate(true)}>
                Yes, change it
              </Button>
            </div>
          )}
          {save.error && <p className="text-sm text-[var(--color-destructive)]">{save.error.message}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || !key || !!parseError || parsed === undefined || dormant || (!!choices && !choices.some((c) => c.value === parsed))}
            >
              Save setting
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The audit log: every call by every person, token and assistant. */
export function AuditPanel() {
  const [hours, setHours] = useState(24);
  const [op, setOp] = useState("");
  const [actor, setActor] = useState("");
  const q = useQuery({
    queryKey: ["audit", hours, op, actor],
    queryFn: () => api<{ entries: AuditEntry[] }>("/api/v1/audit" + qs({ hours, op: op || undefined, actor: actor || undefined, limit: 500 })),
    placeholderData: (p) => p,
  });
  const rows = q.data?.entries ?? [];
  return (
    <Card className="overflow-x-auto">
      <CardHeader className="flex-wrap">
        <CardTitle>Audit log</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-auto" value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label="How far back">
            <option value={1}>Last hour</option>
            <option value={24}>Last 24 hours</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </Select>
          <Input className="h-8 w-44" value={op} onChange={(e) => setOp(e.target.value.trim())} placeholder="Operation, e.g. contacts_get" aria-label="Operation" />
          <Input className="h-8 w-52" value={actor} onChange={(e) => setActor(e.target.value.trim())} placeholder="Who, e.g. token:claude-desktop" aria-label="Who" />
        </div>
      </CardHeader>
      {q.isLoading && <Skeleton className="m-3 h-40" />}
      {!q.isLoading && rows.length === 0 && <Empty title="Nothing in this window." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>When (MT)</TH>
              <TH>Who</TH>
              <TH>Through</TH>
              <TH>Operation</TH>
              <TH>On</TH>
              <TH>Result</TH>
              <TH className="text-right">ms</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((e) => (
              <TR key={e.id}>
                <TD className="num whitespace-nowrap">{formatDateTime(e.occurred_at)}</TD>
                <TD className="max-w-[14rem] truncate">
                  <button className="hover:underline" onClick={() => setActor(e.actor_label)}>
                    {e.actor_label}
                  </button>
                </TD>
                <TD>{e.via}</TD>
                <TD className="num text-xs">
                  <button className="hover:underline" onClick={() => setOp(e.op)}>
                    {e.op}
                  </button>
                </TD>
                <TD>
                  {e.contact_id ? (
                    <Link to="/contacts/$contactId" params={{ contactId: String(e.contact_id) }} className="text-[var(--color-primary)] hover:underline">
                      contact #{e.contact_id}
                    </Link>
                  ) : e.target_type ? (
                    `${e.target_type}${e.target_id ? ` #${e.target_id}` : ""}`
                  ) : (
                    "—"
                  )}
                </TD>
                <TD>
                  {e.status === "ok" ? <span className="text-[var(--color-muted-foreground)]">ok</span> : <Badge variant={e.status === "error" || e.status === "denied" ? "destructive" : "warning"}>{e.status.replace(/_/g, " ")}</Badge>}
                  {e.error && <div className="max-w-[16rem] truncate text-xs text-[var(--color-muted-foreground)]" title={e.error}>{e.error}</div>}
                </TD>
                <TD className="num text-right">{e.duration_ms ?? ""}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

interface DeadEntry {
  entry_id: string;
  kind: string;
  name: string | null;
  store: string;
  received_at: string;
  failed_at: string;
  error: string;
  resolved_at: string | null;
}

/** Database, Valkey, the outage spool, workers, and writes that couldn't be replayed. */
export function HealthPanel() {
  const status = useQuery({ queryKey: ["system-status"], queryFn: () => api<SystemStatus>("/api/v1/system/status"), refetchInterval: 15_000 });
  const dead = useQuery({ queryKey: ["spool-dead"], queryFn: () => api<{ dead: DeadEntry[] }>("/api/v1/system/spool/dead") });
  const s = status.data;
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>System health</CardTitle>
          {s && <ReleaseLabel release={s.release} version={s.version} />}
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 text-sm">
          {!s && <Skeleton className="h-6 w-80" />}
          {s && (
            <>
              <Badge variant={s.database === "up" ? "success" : "destructive"}>Database {s.database}</Badge>
              <Badge variant={s.valkey === "up" ? "success" : "destructive"}>Valkey {s.valkey}</Badge>
              <Badge variant={s.spool.valkey_pending + s.spool.spaces_pending === 0 ? "muted" : "warning"}>
                Outage spool: {s.spool.valkey_pending + s.spool.spaces_pending} waiting
              </Badge>
              <WorkerBadges workers={s.workers} />
            </>
          )}
        </CardContent>
      </Card>
      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>Writes that couldn't be replayed after an outage</CardTitle>
        </CardHeader>
        {(dead.data?.dead ?? []).length === 0 ? (
          <Empty title="None. Everything accepted during outages was applied." />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Received (MT)</TH>
                <TH>What</TH>
                <TH>Why it failed</TH>
                <TH>Status</TH>
              </tr>
            </THead>
            <tbody>
              {dead.data!.dead.map((d) => (
                <TR key={d.entry_id}>
                  <TD className="num whitespace-nowrap">{formatDateTime(d.received_at)}</TD>
                  <TD>
                    {d.kind}
                    {d.name ? `: ${d.name}` : ""}
                  </TD>
                  <TD className="max-w-[28rem] text-xs">{d.error}</TD>
                  <TD>{d.resolved_at ? <Badge variant="muted">resolved</Badge> : <Badge variant="warning">needs a decision</Badge>}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
